/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { FilePond } from 'react-filepond';
import { connect } from 'react-redux';
import { UPLOAD_EVIDENCE } from 'src/api';
import { Label } from 'src/components/atomsLegacy';
import { File, FileUpload } from 'src/components/moleculesLegacy/FileUpload';
import { updateCurrentEvent } from 'src/creators/MCC.creators';
import { textStyleObject } from 'src/theme/atlas';
import { useTheme } from 'styled-components';
import { DateFormats, formatDate, isSameOrBefore } from 'src/utils/time';
import * as Types from '../../../types';
import {
  DatePicker,
  EventPerpetratorList,
  EventTextArea,
  TimePicker,
} from '../../moleculesLegacy';
import { DateAndTime, Files, Form } from './AddEventForm.styles';
import { updateFiles } from './updateFiles';

interface Props {
  setIsAddingPerpetrator: (isAddingPerpetrator: boolean) => void;
  currentEvent: Types.MCCEventType;
  reportID: string;
  updateCurrentEvent: (
    event: Types.MCCEventType,
    key?: string,
    value?: string | any,
  ) => Promise<any>;
  files: File[];
}

export const AddEventFormComponent = (props: Props) => {
  const {
    currentEvent,
    updateCurrentEvent,
    setIsAddingPerpetrator,
    reportID,
    files,
  } = props;
  const theme = useTheme();
  const [changedFiles, setChangedFiles] = useState<File[]>(files);
  const [pond, setPond] = useState<FilePond | null>(null);

  const uploadEvidenceLabelIdle = useMemo(() => {
    const bodySmall = textStyleObject(
      theme.atlas.typography.semantic.bodySmall,
    );
    const caption = textStyleObject(theme.atlas.typography.semantic.caption);
    const typeDefault = theme.atlas.semantic.color.type.default;
    const typeMuted = theme.atlas.semantic.color.type.muted;
    const primaryColor = theme.atlas.semantic.color.action.primary.default;
    const encodedColor = encodeURIComponent(primaryColor);
    const uploadIconSrc = `data:image/svg+xml,%3Csvg width='37' height='32' viewBox='0 0 37 32' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1.15625 20.4899C1.46291 20.4899 1.757 20.6112 1.97384 20.8271C2.19068 21.0429 2.3125 21.3357 2.3125 21.641V27.396C2.3125 28.0065 2.55614 28.592 2.98982 29.0237C3.42349 29.4555 4.01169 29.698 4.625 29.698H32.375C32.9883 29.698 33.5765 29.4555 34.0102 29.0237C34.4439 28.592 34.6875 28.0065 34.6875 27.396V21.641C34.6875 21.3357 34.8093 21.0429 35.0262 20.8271C35.243 20.6112 35.5371 20.4899 35.8438 20.4899C36.1504 20.4899 36.4445 20.6112 36.6613 20.8271C36.8782 21.0429 37 21.3357 37 21.641V27.396C37 28.617 36.5127 29.7881 35.6454 30.6515C34.778 31.5149 33.6016 32 32.375 32H4.625C3.39837 32 2.22199 31.5149 1.35463 30.6515C0.487275 29.7881 0 28.617 0 27.396V21.641C0 21.3357 0.121819 21.0429 0.338658 20.8271C0.555497 20.6112 0.849593 20.4899 1.15625 20.4899Z' fill='${encodedColor}'/%3E%3Cpath d='M17.6814 0.338151C17.7888 0.230962 17.9164 0.145919 18.0568 0.0878936C18.1973 0.0298681 18.3479 0 18.5 0C18.6521 0 18.8027 0.0298681 18.9432 0.0878936C19.0836 0.145919 19.2112 0.230962 19.3186 0.338151L26.2561 7.24418C26.4732 7.46031 26.5952 7.75344 26.5952 8.05909C26.5952 8.36475 26.4732 8.65788 26.2561 8.87401C26.039 9.09013 25.7445 9.21155 25.4375 9.21155C25.1305 9.21155 24.836 9.09013 24.6189 8.87401L19.6562 3.93159V24.1732C19.6562 24.4784 19.5344 24.7712 19.3176 24.987C19.1008 25.2029 18.8067 25.3242 18.5 25.3242C18.1933 25.3242 17.8992 25.2029 17.6824 24.987C17.4656 24.7712 17.3438 24.4784 17.3438 24.1732V3.93159L12.3811 8.87401C12.2736 8.98102 12.146 9.06591 12.0055 9.12383C11.8651 9.18174 11.7145 9.21155 11.5625 9.21155C11.4105 9.21155 11.2599 9.18174 11.1195 9.12383C10.979 9.06591 10.8514 8.98102 10.7439 8.87401C10.6364 8.76699 10.5511 8.63994 10.4929 8.50012C10.4347 8.3603 10.4048 8.21044 10.4048 8.05909C10.4048 7.90775 10.4347 7.75789 10.4929 7.61807C10.5511 7.47824 10.6364 7.3512 10.7439 7.24418L17.6814 0.338151Z' fill='${encodedColor}'/%3E%3C/svg%3E`;
    return `
         <div style="display: flex; flex-direction: column; align-items: center; line-height: 1.45; cursor: pointer; font-family: ${bodySmall.fontFamily}; text-align: center; margin: 0; padding: 0; max-width: 100%;">
         <img src="${uploadIconSrc}" alt="" width="30" height="26" style="display: block; flex-shrink: 0;" />
         <p style="margin: 16px 0 0; padding: 0 8px; color: ${typeDefault}; font-size: ${bodySmall.fontSize}; line-height: ${bodySmall.lineHeight}; letter-spacing: ${bodySmall.letterSpacing};">
         Drag files here or <span class="filepond--label-action" style="color: ${typeDefault}; font-weight: 600; text-decoration: underline; text-underline-offset: 2px;">select files to upload</span>
         </p>
         <p style="margin: 12px 0 0; padding: 0 8px 2px; color: ${typeMuted}; font-size: ${caption.fontSize}; line-height: ${caption.lineHeight}; letter-spacing: ${caption.letterSpacing};">Files can be up to 100MB</p>
         </div>
          `;
  }, [theme]);

  useEffect(() => {
    setChangedFiles(files);
  }, [files]);

  return (
    <Form>
      <DateAndTime>
        <DatePicker
          data-testid="add-event-date"
          id="date"
          name="date"
          label="When did the event take place?"
          value={currentEvent.date.value}
          max={formatDate(new Date(), DateFormats.YYYY_MM_DD)}
          error={
            Boolean(currentEvent.date.value.length) &&
            !isSameOrBefore(
              currentEvent.date.value,
              formatDate(new Date(), DateFormats.YYYY_MM_DD),
            )
          }
          errorMessage={'Invalid date'}
          onChange={async (e) => {
            const timestamp = new Date().toISOString();
            await updateCurrentEvent(
              currentEvent,
              'date',
              currentEvent.date.created
                ? {
                    value: e.target.value,
                    created: currentEvent.date.created,
                    updated: timestamp,
                  }
                : {
                    value: e.target.value,
                    created: timestamp,
                    updated: timestamp,
                  },
            );
          }}
        />
        <TimePicker
          data-testid="add-event-time"
          label="What time did it take place?"
          id="time"
          name="time"
          value={currentEvent.time.value}
          onChange={async (e) => {
            const timestamp = new Date().toISOString();
            await updateCurrentEvent(
              currentEvent,
              'time',
              currentEvent.time.created
                ? {
                    value: e.target.value,
                    created: currentEvent.time.created,
                    updated: timestamp,
                  }
                : {
                    value: e.target.value,
                    created: timestamp,
                    updated: timestamp,
                  },
            );
          }}
        />
      </DateAndTime>
      <EventTextArea
        data-testid="add-event-location"
        label="Where did it happen?"
        value={currentEvent.location.value}
        id="location"
        name="location"
        onChange={async (e) => {
          const timestamp = new Date().toISOString();
          await updateCurrentEvent(
            currentEvent,
            'location',
            currentEvent.location.created
              ? {
                  value: e.target.value,
                  created: currentEvent.location.created,
                  updated: timestamp,
                }
              : {
                  value: e.target.value,
                  created: timestamp,
                  updated: timestamp,
                },
          );
        }}
      />
      <EventTextArea
        data-testid="add-event-description"
        label="What happened, in the reporter's own words?"
        value={currentEvent.description.value}
        id="description"
        name="description"
        rows={8}
        required
        onChange={async (e) => {
          const timestamp = new Date().toISOString();
          await updateCurrentEvent(
            currentEvent,
            'description',
            currentEvent.description.created
              ? {
                  value: e.target.value,
                  created: currentEvent.description.created,
                  updated: timestamp,
                }
              : {
                  value: e.target.value,
                  created: timestamp,
                  updated: timestamp,
                },
          );
        }}
      />
      <EventTextArea
        data-testid="add-event-emotion"
        label="What was the impact? How did the reporter feel?"
        value={currentEvent.emotion.value}
        id="emotion"
        name="emotion"
        rows={8}
        onChange={async (e) => {
          const timestamp = new Date().toISOString();
          await updateCurrentEvent(
            currentEvent,
            'emotion',
            currentEvent.emotion.created
              ? {
                  value: e.target.value,
                  created: currentEvent.emotion.created,
                  updated: timestamp,
                }
              : {
                  value: e.target.value,
                  created: timestamp,
                  updated: timestamp,
                },
          );
        }}
      />
      <EventTextArea
        data-testid="add-event-witness"
        label="Did anyone else see what happened? Did the reporter tell anyone?"
        value={currentEvent.witness.value}
        id="witness"
        name="witness"
        rows={4}
        onChange={async (e) => {
          const timestamp = new Date().toISOString();
          await updateCurrentEvent(
            currentEvent,
            'witness',
            currentEvent.witness.created
              ? {
                  value: e.target.value,
                  created: currentEvent.witness.created,
                  updated: timestamp,
                }
              : {
                  value: e.target.value,
                  created: timestamp,
                  updated: timestamp,
                },
          );
        }}
      />
      <Files data-testid="upload-evidence">
        <Label>Upload supporting evidence or files</Label>
        <FileUpload
          eventId={currentEvent.id}
          files={changedFiles}
          server={`${UPLOAD_EVIDENCE(reportID)}`}
          labelIdle={uploadEvidenceLabelIdle}
          setPond={setPond}
          onprocessfiles={async () => {
            if (pond !== null) {
              await updateFiles(
                pond,
                setChangedFiles,
                updateCurrentEvent,
                currentEvent,
              );
            }
          }}
          onupdatefiles={async () => {
            if (pond !== null) {
              await updateFiles(
                pond,
                setChangedFiles,
                updateCurrentEvent,
                currentEvent,
              );
            }
          }}
          onremovefile={async () => {
            if (pond !== null) {
              await updateFiles(
                pond,
                setChangedFiles,
                updateCurrentEvent,
                currentEvent,
              );
            }
          }}
        />
      </Files>
      <EventPerpetratorList
        data-testid="event-perpetrator-list"
        label="Who did this?"
        perpetrators={currentEvent.perpetrators}
        onClick={() => {
          setIsAddingPerpetrator(true);
        }}
      />
    </Form>
  );
};

const mapDispatchToProps = { updateCurrentEvent };

export const AddEventForm = connect(
  null,
  mapDispatchToProps,
)(AddEventFormComponent);
